import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import { capitalize } from 'lodash/fp'

import { media } from '../theme'
import AnimatedSparking from './AnimatedSparking'

const fadeAnimation = keyframes`
  0%,
  100% {
    opacity: 0;
  }

  50% {
    opacity: 0.3;
  }
`

const Container = styled.div`
  cursor: pointer;
  height: 100%;
  position: relative;
  width: 100%;
`

const Children = styled.div`
  pointer-events: none;
`

const SVG = styled.svg`
  animation: ${fadeAnimation} 1700ms infinite;
  animation-timing-function: cubic-bezier(0.79, 0, 0.3, 0.98);
  opacity: 0;
  left: 0;
  overflow: visible;
  position: absolute;
  top: 0;
  transform-origin: ${p => p.transformOrigin || 'auto'};
  width: 100%;
`

const animationChooser = ({ theme, highlight, highlightType }) => {
  if (!highlight) return 'none'

  switch (highlightType) {
    case 'success':
      return `0.5s ${theme.animation.explodingStroke} forwards`

    default:
      return 'none'
  }
}

const Polygon = styled.polygon`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  animation: ${animationChooser};
  cursor: ${props =>
    props.onClick && props.highlightType ? 'pointer' : 'auto'};
  pointer-events: auto;
`

const Path = Polygon.withComponent('path')

const AnimatedSparkingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  visibility: ${p => (p.active ? 'visible' : 'hidden')};
`

const StyledAnimatedSparking = styled(AnimatedSparking)`
  position: absolute;

  top: ${p => p.top}${p => (p.usePixels ? 'px' : '%')};
  left: ${p => p.left}${p => (p.usePixels ? 'px' : '%')};
`

/**
 * Set interaction handlers within a non-rectangular area. The component
 * accepts SVG `path` or `polygon` point values to define the path. The path
 * can be highlighted to identify the interactable area.
 */
export default class IrregularInteraction extends Component {
  static highlightTypes = ['pending', 'success']

  static propTypes = {
    /** Style the path to highlight the interactable area if true. */
    highlight: PropTypes.bool,

    /** The type of highlight style to apply. */
    highlightType: PropTypes.oneOf(IrregularInteraction.highlightTypes),

    /** Function called upon clicking within the path. */
    onClick: PropTypes.func,

    /** Function called upon mouse over within the path. */
    onMouseOver: PropTypes.func,

    /** SVG path for the interactable area. */
    path: PropTypes.string,

    /** SVG polygon points for the interactable area. */
    points: PropTypes.string,

    /** SVG `preserveAspectRatio` for setting sizing on the interactable area. */
    preserveAspectRatio: PropTypes.string,

    /** CSS transform origin property **/
    transformOrigin: PropTypes.string,

    /** SVG x value for `translate` for positioning the interactable area. */
    translateX: PropTypes.number,

    /** SVG y value for `translate` for positioning the interactable area. */
    translateY: PropTypes.number,

    /** SVG `viewBox` for positioning the interactable area. */
    viewBox: PropTypes.string,
  }

  static defaultProps = {
    highlight: false,
    highlightType: IrregularInteraction.highlightTypes[0],
    translateX: 0,
    translateY: 0,
  }

  renderSvg() {
    const {
      highlight,
      highlightType,
      onClick,
      onMouseOver,
      path,
      points,
      preserveAspectRatio,
      transformOrigin,
      translateX,
      translateY,
      viewBox,
    } = this.props

    const highlightColor = highlight && `highlight${capitalize(highlightType)}`
    const OutlineComponent = points ? Polygon : Path

    return (
      <SVG
        viewBox={viewBox}
        preserveAspectRatio={preserveAspectRatio}
        transformOrigin={transformOrigin}
      >
        <g transform={`translate(${translateX}, ${translateY})`}>
          <OutlineComponent
            fill={
              highlight && highlightType === 'pending' ? 'red' : 'transparent'
            }
            highlight={highlight}
            highlightColor={highlightColor}
            highlightType={highlightType}
            onMouseOver={onMouseOver}
            d={path}
            points={points}
          />
        </g>
      </SVG>
    )
  }

  render() {
    const {
      children,
      highlight,
      highlightType,
      showSparks = true,
      sparksTop = 0,
      sparksLeft = 0,
      sparksRotation = 0,
      sparksPercentWidth = '35%',
      usePixels = false,
      ...props
    } = this.props

    return (
      <Container {...props}>
        <Children>{children}</Children>
        {this.renderSvg()}
        <AnimatedSparkingContainer
          active={showSparks && highlight && highlightType === 'pending'}
        >
          <StyledAnimatedSparking
            top={sparksTop}
            left={sparksLeft}
            rotation={sparksRotation}
            percentWidth={sparksPercentWidth}
            usePixels={usePixels}
          />
        </AnimatedSparkingContainer>
      </Container>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/IrregularInteraction.js